import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Inbox,
  Check,
  X,
  Clock,
  ChevronRight,
  Replace,
  Sparkles,
  ShieldCheck,
  Calendar,
  IndianRupee,
} from "lucide-react";
import { SEED_SUGGESTIONS, type AgentSuggestion } from "../../data/suggestions";
import { env } from "../../lib/env";
import { cls } from "../../lib/cls";

/* ──────────────────────────────────────────────────────────────────
   InboxQueue — the designer's approval surface. Every agent-fired
   suggestion lands here; accept / reject / snooze ticks the rule's
   metrics in real-time, which feeds the Memory promoter.

   Mirrors the production agent's `tasks/send` envelope: each card is
   one A2A Task in `state: working` awaiting designer disposition.
   ────────────────────────────────────────────────────────────────── */

type Decision = "accepted" | "rejected" | "snoozed";

interface InboxState {
  decisions: Record<string, Decision>;
}

interface InboxQueueProps {
  onFocusNode?: (id: string) => void;
}

const ICON_BY_KIND: Record<AgentSuggestion["kind"], JSX.Element> = {
  substitute:        <Replace className="w-3.5 h-3.5" />,
  redistribute:      <IndianRupee className="w-3.5 h-3.5" />,
  restyle:           <Sparkles className="w-3.5 h-3.5" />,
  "approval-packet": <ShieldCheck className="w-3.5 h-3.5" />,
  flag:              <Calendar className="w-3.5 h-3.5" />,
};

function loadState(): InboxState {
  try {
    const raw = localStorage.getItem(env.VITE_LS_INBOX_DECISIONS);
    if (!raw) return { decisions: {} };
    return JSON.parse(raw) as InboxState;
  } catch { return { decisions: {} }; }
}

export function InboxQueue({ onFocusNode }: InboxQueueProps): JSX.Element {
  const [state, setState] = useState<InboxState>(() => loadState());

  useEffect(() => {
    try { localStorage.setItem(env.VITE_LS_INBOX_DECISIONS, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  function decide(id: string, decision: Decision): void {
    setState((s) => ({ ...s, decisions: { ...s.decisions, [id]: decision } }));
  }

  const open = useMemo(
    () => SEED_SUGGESTIONS.filter((s) => !state.decisions[s.id]),
    [state.decisions],
  );
  const closed = useMemo(
    () => SEED_SUGGESTIONS.filter((s) => state.decisions[s.id]),
    [state.decisions],
  );

  return (
    <div className="panel p-4 space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-ink-950 text-cream grid place-items-center">
            <Inbox className="w-3.5 h-3.5" />
          </span>
          <div>
            <div className="text-sm font-semibold text-ink-950 leading-tight">Suggestions queue</div>
            <div className="text-[11px] text-ink-500 leading-tight">
              {open.length} awaiting decision · {closed.length} resolved
            </div>
          </div>
        </div>
        <span className="pill bg-foyr/10 text-foyr-deep tabular text-[10px]">{open.length}</span>
      </header>

      {open.length === 0 ? (
        <div className="text-[12px] text-ink-500 italic px-1 py-3">
          Nothing waiting. New suggestions land here the moment a rule fires.
        </div>
      ) : (
        <ul className="space-y-2">
          <AnimatePresence initial={false}>
            {open.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                onAccept={() => decide(s.id, "accepted")}
                onReject={() => decide(s.id, "rejected")}
                onSnooze={() => decide(s.id, "snoozed")}
                {...(onFocusNode ? { onFocusNode } : {})}
              />
            ))}
          </AnimatePresence>
        </ul>
      )}

      {closed.length > 0 ? (
        <details className="border-t border-border pt-2">
          <summary className="cursor-pointer text-[11px] text-ink-500 select-none hover:text-ink-950">
            Resolved · {closed.length}
          </summary>
          <ul className="mt-2 space-y-1">
            {closed.map((s) => {
              const d = state.decisions[s.id];
              return (
                <li key={s.id} className="flex items-center gap-1.5 text-[11px]">
                  <span className={cls(
                    "pill text-[9px]",
                    d === "accepted" ? "bg-fern/10 text-fern" :
                    d === "rejected" ? "bg-foyr/10 text-foyr-deep" :
                                       "bg-cream-dark border border-border text-ink-700",
                  )}>
                    {d}
                  </span>
                  <span className="text-ink-700 truncate">{s.title}</span>
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onAccept,
  onReject,
  onSnooze,
  onFocusNode,
}: {
  suggestion: AgentSuggestion;
  onAccept: () => void;
  onReject: () => void;
  onSnooze: () => void;
  onFocusNode?: ((id: string) => void) | undefined;
}): JSX.Element {
  const icon = ICON_BY_KIND[suggestion.kind];
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 60 }}
      transition={{ duration: 0.22 }}
      className="panel-subtle p-3 space-y-2"
    >
      <header className="flex items-start gap-2">
        <span className="w-6 h-6 rounded-md bg-foyr text-cream grid place-items-center shrink-0">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-ink-950 leading-tight">{suggestion.title}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink-500 mt-0.5">
            <span className="font-mono">{suggestion.firedByRuleId}</span> · proposed {suggestion.proposedAgo}
          </div>
        </div>
      </header>

      <p className="text-[11px] text-ink-700 leading-snug">{suggestion.rationale}</p>

      {suggestion.delta && suggestion.delta.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {suggestion.delta.map((d) => (
            <span
              key={d.label}
              className={cls(
                "pill text-[10px] tabular",
                d.tone === "money" ? "bg-foyr/10 text-foyr-deep" :
                d.tone === "time"  ? "bg-sea/10 text-sea" :
                                     "bg-mulberry/10 text-mulberry",
              )}
            >
              {d.label}
            </span>
          ))}
        </div>
      ) : null}

      {suggestion.preview ? (
        <div className="text-[10px] font-mono text-ink-500 bg-cream/70 border border-border rounded-md px-2 py-1 leading-tight">
          {suggestion.preview}
        </div>
      ) : null}

      <div className="flex items-center gap-1.5">
        {onFocusNode && suggestion.affectsNodeIds[0] ? (
          <button
            onClick={() => onFocusNode(suggestion.affectsNodeIds[0]!)}
            className="text-[10px] text-ink-500 hover:text-ink-950 inline-flex items-center gap-1 underline-offset-2 hover:underline shrink-0"
          >
            see on map <ChevronRight className="w-3 h-3" />
          </button>
        ) : null}
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={onSnooze} className="btn btn-ghost !text-[10px] !py-1 !px-2" title="Snooze">
            <Clock className="w-3 h-3" /> Snooze
          </button>
          <button onClick={onReject} className="btn btn-outline !text-[10px] !py-1 !px-2" title="Reject">
            <X className="w-3 h-3" /> Reject
          </button>
          <button onClick={onAccept} className="btn btn-foyr !text-[10px] !py-1 !px-2" title="Accept">
            <Check className="w-3 h-3" /> Accept
          </button>
        </div>
      </div>
    </motion.li>
  );
}
